import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiPlus, FiTrash2, FiUser, FiPackage, FiSearch, FiCalendar, FiFileText, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Toast } from '../../components/UI/Toast';
import { ordenesService } from '../../services/ordenesService';
import { useClientes } from '../../hooks/useClientes';
import { useProductos } from '../../hooks/useProductos';
import { useAuth } from '../../contexts/AuthContext';
import './PreOrderForm.scss';

const PreOrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const isEditing = Boolean(id);
  const dropdownRef = useRef(null);

  // Obtener la fuente desde el state de navegación
  const sourceFromState = location.state?.source || 'BETEL_NATURAL';

  const { clientes, loadClientes } = useClientes();
  const { productos: allProductos, searchProductos, isLoading: productosLoading } = useProductos();

  const [orden, setOrden] = useState({
    client_id: '',
    sale_date: new Date().toISOString().split('T')[0],
    notes: '',
    general_notes: '',
    items: [],
    status: 'ON_HOLD', // Estatus por defecto: DETENIDA
    order_type: 'PRE_ORDER',
    source: sourceFromState,
    order_status: 'DETENIDA', // LIBERADA | DETENIDA (por defecto DETENIDA)
    taken_by: user?.full_name || '',
    reviewed_by: '',
    user_id: user?.id || ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  // Estados para búsqueda de productos
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);

  useEffect(() => {
    loadClientes();
  }, []);

  useEffect(() => {
    if (isEditing && id) {
      loadOrden();
    }
  }, [id, isEditing]);

  // Efecto para búsqueda de productos
  useEffect(() => {
    if (productSearch.trim().length > 0) {
      searchProductos(productSearch);
      setShowDropdown(true);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
    }
  }, [productSearch]);

  // Actualizar resultados cuando cambien los productos
  useEffect(() => {
    if (productSearch.trim().length > 0 && showDropdown) {
      setSearchResults(allProductos);
    }
  }, [allProductos, productSearch, showDropdown]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadOrden = async () => {
    setIsLoading(true);
    const result = await ordenesService.getOrdenById(id);
    if (result.success) {
      const ordenData = {
        ...result.data,
        order_type: result.data.order_type || 'PRE_ORDER',
        source: result.data.source || sourceFromState,
        order_status: result.data.order_status || 'DETENIDA',
        taken_by: result.data.taken_by || '',
        reviewed_by: result.data.reviewed_by || ''
      };
      setOrden(ordenData);
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleInputChange = (field, value) => {
    setOrden(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpiar error de validación si existe
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleProductSelect = (product) => {
    // Cerrar dropdown inmediatamente
    setShowDropdown(false);
    setSearchResults([]);

    // Establecer producto seleccionado
    setSelectedProduct(product);

    // Limpiar campo de búsqueda
    setProductSearch('');
  };

  const addItemToOrden = () => {
    if (!selectedProduct || itemQuantity <= 0) {
      setError('Seleccione un producto y especifique una cantidad válida');
      return;
    }

    const newItem = {
      product_id: selectedProduct.id,
      product_name: selectedProduct.nombreProducto,
      product_code: selectedProduct.codigo,
      quantity: itemQuantity,
      quantity_requested: itemQuantity,
      unit_price: selectedProduct.precio || 0,
      validation_status: 'PENDING',
      stock_available: selectedProduct.stock || 0
    };

    setOrden(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Limpiar formulario de producto
    setSelectedProduct(null);
    setProductSearch('');
    setItemQuantity(1);
    setError(null);
  };

  const removeItem = (index) => {
    setOrden(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!orden.client_id) {
      errors.client_id = 'Cliente es requerido';
    }
    if (!orden.sale_date) {
      errors.sale_date = 'Fecha es requerida';
    }
    if (orden.items.length === 0) {
      errors.items = 'Debe agregar al menos un producto';
    }

    setValidationErrors(errors);

    // Si hay errores, construir mensaje y mostrar toast
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).filter(msg => msg);
      const toastMsg = `Por favor corrige los siguientes errores: ${errorMessages.join(', ')}`;
      setToastMessage(toastMsg);
      setToastType('error');
      setShowToast(true);
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const ordenData = {
      ...orden,
      client_id: parseInt(orden.client_id),
      user_id: parseInt(orden.user_id),
      order_type: 'PRE_ORDER',
      sale_type: 'PRODUCT',
      priority: 'MEDIUM',
      subtotal: 0,
      tax_amount: 0,
      discount_amount: 0,
      total: 0
    };

    console.log('📦 Datos de PRE-ORDEN a enviar:', ordenData);

    const result = isEditing
      ? await ordenesService.updateOrden(id, ordenData)
      : await ordenesService.createOrden(ordenData);

    if (result.success) {
      navigate('/system/ventas');
    } else {
      setError(result.message);
    }
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate('/system/ventas');
  };

  const getSourceLabel = (source) => {
    const labels = {
      'BETEL_NATURAL': 'Betel Natural',
      'NATURALAND_LAB': 'Naturaland Lab',
      'BETEL_NATURAL_VIERNES': 'Betel Natural Viernes'
    };
    return labels[source] || source;
  };

  const getSourceIcon = (source) => {
    const icons = {
      'BETEL_NATURAL': '🌿',
      'NATURALAND_LAB': '🧪',
      'BETEL_NATURAL_VIERNES': '📅'
    };
    return icons[source] || '📦';
  };

  if (isLoading) {
    return <LoadingGrid message={isEditing ? "Cargando pre-orden..." : "Preparando formulario..."} />;
  }

  return (
    <div className="preorder-form-page">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={handleCancel}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          {isEditing ? 'Editar Pre-Orden' : 'Nueva Pre-Orden'}
        </h1>

        <div className="header-actions">
          <button className="btn btn--outline" onClick={handleCancel}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            <FiSave size={16} />
            {isEditing ? 'Actualizar' : 'Crear Pre-Orden'}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="preorder-form">
        {/* Card 1: Información de la Pre-Orden */}
        <div className="preorder-header-card">
          <div className="preorder-source-badge">
            <span className="source-icon">{getSourceIcon(orden.source)}</span>
            <span className="source-label">{getSourceLabel(orden.source)}</span>
          </div>

          {/* Notas Generales */}
          <div className="general-notes-box">
            <div className="general-notes-header">NOTAS GENERALES:</div>
            <div className="general-notes-content">
              <div className="general-notes-section">
                <strong>TIEMPO META ENTREGAS</strong>
                <ul>
                  <li>"Órdenes chicas y de online" que llegan antes de las 12 pm → Salir mismo día</li>
                  <li>"Órdenes medianas" → Salir entre 2-3 días</li>
                  <li>"Órdenes grandes" → Salir de 3 a 5 días</li>
                </ul>
              </div>
              <div className="general-notes-checks">
                <div className="check-item">☑ Check for B-day Info</div>
                <div className="check-item">☑ Check for CA Seller's permit paper</div>
              </div>
            </div>
          </div>

          <div className="preorder-header-grid">
            {/* Cliente */}
            <div className="preorder-field">
              <label className="preorder-label">Cliente *</label>
              <select
                className={`preorder-input ${validationErrors.client_id ? 'preorder-input--error' : ''}`}
                value={orden.client_id}
                onChange={(e) => handleInputChange('client_id', parseInt(e.target.value) || '')}
                required
              >
                <option value="">Seleccionar cliente...</option>
                {clientes.map(cliente => (
                  <option key={cliente.id} value={cliente.id}>
                    {cliente.nombreNegocio}
                  </option>
                ))}
              </select>
              {validationErrors.client_id && (
                <span className="preorder-error">{validationErrors.client_id}</span>
              )}
            </div>

            {/* Fecha */}
            <div className="preorder-field">
              <label className="preorder-label">Fecha de Orden *</label>
              <input
                type="date"
                className={`preorder-input ${validationErrors.sale_date ? 'preorder-input--error' : ''}`}
                value={orden.sale_date}
                onChange={(e) => handleInputChange('sale_date', e.target.value)}
                required
              />
              {validationErrors.sale_date && (
                <span className="preorder-error">{validationErrors.sale_date}</span>
              )}
            </div>

            {/* Estatus de Orden */}
            <div className="preorder-field">
              <label className="preorder-label">Estatus de Orden *</label>
              <div className="status-buttons">
                <button
                  type="button"
                  className={`status-btn ${orden.order_status === 'LIBERADA' ? 'status-btn--active status-btn--released' : ''}`}
                  onClick={() => handleInputChange('order_status', 'LIBERADA')}
                >
                  <FiCheckCircle size={18} />
                  LIBERADA
                </button>
                <button
                  type="button"
                  className={`status-btn ${orden.order_status === 'DETENIDA' ? 'status-btn--active status-btn--stopped' : ''}`}
                  onClick={() => handleInputChange('order_status', 'DETENIDA')}
                >
                  <FiAlertCircle size={18} />
                  DETENIDA
                </button>
              </div>
            </div>

            {/* Nombre de quien tomó la orden */}
            <div className="preorder-field">
              <label className="preorder-label">Nombre de quien tomó la orden</label>
              <input
                type="text"
                className="preorder-input"
                value={orden.taken_by}
                onChange={(e) => handleInputChange('taken_by', e.target.value)}
                placeholder="Nombre completo (opcional)"
              />
            </div>

            {/* Nombre de quien revisó la orden (solo visible al convertir a ORDER) */}
            {orden.order_type === 'ORDER' && (
              <div className="preorder-field">
                <label className="preorder-label">Nombre de quien revisó la orden</label>
                <input
                  type="text"
                  className="preorder-input"
                  value={orden.reviewed_by}
                  onChange={(e) => handleInputChange('reviewed_by', e.target.value)}
                  placeholder="Nombre completo (opcional)"
                />
              </div>
            )}

            {/* Notas - Ocupa 2 columnas */}
            <div className="preorder-field preorder-field--full">
              <label className="preorder-label">Notas de la Orden</label>
              <textarea
                className="preorder-input preorder-textarea"
                placeholder="Notas adicionales específicas de esta orden..."
                value={orden.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* Card 2: Buscador de Productos */}
        <div className="product-search-card">
          <div className="product-search-header">
            <div className="search-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="search-header-content">
              <h3 className="search-header-title">Agregar Productos</h3>
              <p className="search-header-subtitle">Busque y agregue productos a la pre-orden</p>
            </div>
          </div>

          <div className="product-search-body">
            {/* Buscador Principal */}
            <div className="search-combo-wrapper" ref={dropdownRef}>
              <div className="search-combo-icon-left">
                <FiSearch size={20} />
              </div>
              <input
                type="text"
                className="search-combo-input"
                placeholder="Buscar por nombre de producto o código..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowDropdown(true);
                  }
                }}
              />
              {productosLoading && (
                <div className="search-combo-loading">
                  <div className="spinner-sm"></div>
                </div>
              )}

              {/* Dropdown de resultados */}
              {showDropdown && searchResults.length > 0 && (
                <div className="product-dropdown">
                  {searchResults.map(product => (
                    <div
                      key={product.id}
                      className="product-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleProductSelect(product);
                      }}
                    >
                      <div className="product-dropdown-icon">
                        <FiPackage size={20} />
                      </div>
                      <div className="product-dropdown-info">
                        <div className="product-dropdown-name">{product.nombreProducto}</div>
                        <div className="product-dropdown-meta">
                          <span className="meta-stock">Stock: {product.stock}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vista previa del producto seleccionado */}
            {selectedProduct && (
              <div className="selected-product-card">
                <div className="selected-product-info">
                  <div className="selected-product-badge">
                    <FiPackage size={16} />
                  </div>
                  <div className="selected-product-details">
                    <h4 className="selected-product-name">{selectedProduct.nombreProducto}</h4>
                    <div className="selected-product-meta">
                      <span className="meta-item">Stock: {selectedProduct.stock}</span>
                    </div>
                  </div>
                </div>

                <div className="selected-product-controls">
                  <div className="control-group">
                    <label className="control-label">Cantidad</label>
                    <input
                      type="number"
                      value={itemQuantity}
                      onChange={(e) => setItemQuantity(parseFloat(e.target.value) || 1)}
                      min="1"
                      step="1"
                      className="control-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--add-product"
                    onClick={addItemToOrden}
                  >
                    <FiPlus size={18} />
                    Agregar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Lista de Productos */}
        {orden.items.length > 0 ? (
          <div className="preorder-products-card">
            <div className="products-card-header">
              <div className="products-header-icon">
                <FiPackage size={24} />
              </div>
              <div className="products-header-content">
                <h3 className="products-header-title">Productos en la Pre-Orden</h3>
                <p className="products-header-subtitle">{orden.items.length} producto(s) agregado(s)</p>
              </div>
            </div>

            <div className="products-list">
              {orden.items.map((item, index) => (
                <div key={index} className="product-item">
                  <div className="product-item-number">{index + 1}.</div>
                  <div className="product-item-info">
                    <div className="product-item-name">{item.product_name}</div>
                  </div>
                  <div className="product-item-quantity">
                    <span className="quantity-label">Cantidad:</span>
                    <span className="quantity-value">{item.quantity}</span>
                  </div>
                  <button
                    type="button"
                    className="product-item-delete"
                    onClick={() => removeItem(index)}
                    title="Eliminar producto"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            {validationErrors.items && (
              <div className="products-error">{validationErrors.items}</div>
            )}
          </div>
        ) : (
          <div className="preorder-empty-state">
            <div className="empty-state-icon">
              <FiPackage size={64} />
            </div>
            <h3 className="empty-state-title">No hay productos agregados</h3>
            <p className="empty-state-message">
              Use el buscador de productos para encontrar y agregar ítems a la pre-orden
            </p>
            {validationErrors.items && (
              <div className="products-error">{validationErrors.items}</div>
            )}
          </div>
        )}
      </form>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default PreOrderForm;
